import * as Yup from "yup";

// Validation patterns derived from backend schema and UAE requirements
// Source: neurula-backend/app/schemas/users.py

// Email pattern - standard email validation
const emailRegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Phone pattern - supports UAE format with country code, spaces, dashes, parentheses
const phoneRegExp = /^[\d\s\+\-\(\)]{10,15}$/;

// Name patterns - letters, spaces, periods, apostrophes, hyphens (common in international names)
const nameRegExp = /^[a-zA-Z\s\.'\-]{2,50}$/;

// Emirates ID pattern - exactly 15 digits starting with 784 as per UAE standard
const emiratesIdRegExp = /^784\d{12}$/;

// Employee ID pattern - alphanumeric with hyphens, 3-20 characters
const employeeIdRegExp = /^[A-Z0-9\-]{3,20}$/;

// License Number pattern - alphanumeric with hyphens/slashes, 5-30 characters
const licenseNumberRegExp = /^[A-Z0-9\-\/]{5,30}$/;

// Numeric only pattern for fields that should only contain digits
const numericRegExp = /^\d+$/;

// export const addStaffSchema = Yup.object().shape({
//   firstName: Yup.string()
//     .min(2, "Too Short Name!")
//     .max(50, "Too Long Name!")
//     .required("First name is required."),
//   lastName: Yup.string()
//     .min(2, "Too Short Name!")
//     .max(50, "Too Long Name!")
//     .required("Last name is required."),
//   email: Yup.string().email("Invalid email").required("Email is required."),
//   mobileNumber: Yup.string()
//     .matches(phoneRegExp, " only Numeric values allowed")
//     .required("Phone number is required."),
//   password: Yup.string()
//     .required("Password is required."),
//     // .max(18, "Password is too long - should be maximum 20 chars.")
//     // .min(7, "Password is too short - should be 8 chars minimum.")
//     // .matches(/[0-9]/, "Password requires a number")
//     // .matches(/[a-z]/, "Password requires a lowercase letter")
//     // .matches(/[A-Z]/, "Password requires an uppercase letter")
//     // .matches(/[^\w]/, "Password requires a special chars"),
//   confirmPassword: Yup.string()
//     .oneOf(
//       [Yup.ref("password"), null],
//       "Password and confirm password does not match"
//     )
//     .required("Confirm password is required."),
//   address: Yup.string().required("Address is required."),
//   city: Yup.string().required("City is required."),
//   state: Yup.string().required("State is required."),
//   employeeId: Yup.string().required("Employee-Id is required."),

//   licenceNumber: Yup.string().required("Licence Number  is required."),
//   emiratesIdNumber: Yup.string().required("Emirates Id is required."),
//   designation: Yup.string().required("Designation is required."),
//   DateOfJoining: Yup.string().required("Date Of Joining is required."),
//   gender: Yup.string().required("Gender is required."),
//   userRoleId : Yup.array()
//     .min(1, "At least one role must be selected")
//     .required("This field is required"),
//   stampImage: Yup.mixed()
//     .required("Stamp image is required.")
//     .test("fileType", "Unsupported file format", (value) => {
//       return (
//         value &&
//         [
//           "image/gif",
//           "image/png",
//           "image/jpeg",
//           "image/jpg",
//           "image/svg",
//           "image/webp",
//         ].includes(value.type)
//       );
//     }),
//   photo: Yup.mixed()
//     .required("Photo is required.")
//     .test("fileType", "Unsupported file format", (value) => {
//       return (
//         value &&
//         [
//           "image/gif",
//           "image/png",
//           "image/jpeg",
//           "image/jpg",
//           "image/svg",
//           "image/webp",
//         ].includes(value.type)
//       );
//     }),
//   signature: Yup.mixed()
//     .required("Signature image is required.")
//     .test("fileType", "Unsupported file format", (value) => {
//       return (
//         value &&
//         [
//           "image/gif",
//           "image/png",
//           "image/jpeg",
//           "image/jpg",
//           "image/svg",
//           "image/webp",
//         ].includes(value.type)
//       );
//     }),
// });

export const addStaffSchema = Yup.object().shape({
  // Personal Information - Required fields only
  first_name: Yup.string()
    .matches(nameRegExp, "First name can only contain letters, spaces, apostrophes, periods and hyphens")
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must be less than 50 characters")
    .required("First name is required"),
  
  last_name: Yup.string()
    .matches(nameRegExp, "Last name can only contain letters, spaces, apostrophes, periods and hyphens")
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name must be less than 50 characters")
    .required("Last name is required"),
  
  middle_name: Yup.string()
    .nullable()
    .transform((value) => value === '' ? null : value)
    .test('middle_name_format', 'Middle name can only contain letters, spaces, apostrophes, periods and hyphens', 
      function(value) {
        if (!value) return true; // Allow empty/null values
        return nameRegExp.test(value);
      })
    .max(50, "Middle name must be less than 50 characters"),
  
  // Contact Information - Required fields
  email: Yup.string()
    .matches(emailRegExp, "Please enter a valid email address")
    .required("Email is required"),
  
  phone_number: Yup.string()
    .matches(numericRegExp, "Phone number must contain only digits")
    .min(7, "Phone number must be at least 7 digits")
    .max(15, "Phone number must be less than 15 digits")
    .required("Phone number is required"),
  
  phone_country_code: Yup.string().required("Country code is required"),
  
  // Security - Required fields  
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/, "Password must contain uppercase, lowercase, number and special character")
    .required("Password is required"),
  
  confirm_password: Yup.string()
    .oneOf([Yup.ref('password'), null], "Password and confirm password must match")
    .required("Confirm password is required"),
  
  // Professional Information - Required fields
  department: Yup.object().shape({
    value: Yup.string().required("Department is required"),
    label: Yup.string().required("Department is required")
  }).nullable().required("Department is required"),
  
  roles: Yup.array().of(
    Yup.object().shape({
      value: Yup.string().required("Role value is required"),
      label: Yup.string().required("Role label is required")
    })
  ).min(1, "At least one role must be selected").required("Roles are required"),
  
  specialty: Yup.string()
    .min(2, "Specialty must be at least 2 characters")
    .max(100, "Specialty must be less than 100 characters")
    .required("Specialty is required"),
  
  // Optional fields - validated only if provided
  designation: Yup.string()
    .nullable()
    .transform((value) => value === '' ? null : value)
    .test('designation_length', 'Designation must be at least 2 characters', 
      function(value) {
        if (!value) return true; // Allow empty/null values
        return value.length >= 2;
      })
    .max(100, "Designation must be less than 100 characters"),
  
  employee_id: Yup.string()
    .matches(employeeIdRegExp, "Employee ID must be 3-20 characters, containing only letters, numbers and hyphens")
    .required("Employee ID is required"),
  
  emirates_id_number: Yup.string()
    .matches(emiratesIdRegExp, "Emirates ID must be exactly 15 digits starting with 784")
    .required("Emirates ID is required"),
  
  license_number: Yup.string()
    .nullable()
    .transform((value) => value === '' ? null : value)
    .test('license_format', 'License number must be 5-30 characters, containing only letters, numbers, hyphens and slashes', 
      function(value) {
        if (!value) return true; // Allow empty/null values
        return licenseNumberRegExp.test(value);
      }),
  
  address: Yup.string()
    .nullable()
    .transform((value) => value === '' ? null : value)
    .test('address_length', 'Address must be at least 5 characters', 
      function(value) {
        if (!value) return true; // Allow empty/null values
        return value.length >= 5;
      })
    .max(200, "Address must be less than 200 characters"),
  
  city: Yup.string()
    .nullable()
    .transform((value) => value === '' ? null : value)
    .test('city_format', 'City name can only contain letters, spaces, apostrophes, periods and hyphens', 
      function(value) {
        if (!value) return true; // Allow empty/null values
        return nameRegExp.test(value);
      })
    .test('city_length', 'City must be at least 2 characters', 
      function(value) {
        if (!value) return true; // Allow empty/null values
        return value.length >= 2;
      })
    .max(50, "City must be less than 50 characters"),
  
  state: Yup.string()
    .nullable()
    .transform((value) => value === '' ? null : value)
    .test('state_format', 'State name can only contain letters, spaces, apostrophes, periods and hyphens', 
      function(value) {
        if (!value) return true; // Allow empty/null values
        return nameRegExp.test(value);
      })
    .test('state_length', 'State must be at least 2 characters', 
      function(value) {
        if (!value) return true; // Allow empty/null values
        return value.length >= 2;
      })
    .max(50, "State must be less than 50 characters"),
  
  gender: Yup.string()
    .oneOf(["1", "2", "3"], "Please select a valid gender")
    .required("Gender is required"),
  
  date_of_joining: Yup.string()
    .required("Date of joining is required")
});

export const editStaffSchema = Yup.object().shape({
  // Personal Information - Required fields
  first_name: Yup.string()
    .matches(nameRegExp, "First name can only contain letters, spaces, apostrophes, periods and hyphens")
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must be less than 50 characters")
    .required("First name is required"),
  
  last_name: Yup.string()
    .matches(nameRegExp, "Last name can only contain letters, spaces, apostrophes, periods and hyphens")
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name must be less than 50 characters")
    .required("Last name is required"),
  
  middle_name: Yup.string()
    .matches(nameRegExp, "Middle name can only contain letters, spaces, apostrophes, periods and hyphens")
    .max(50, "Middle name must be less than 50 characters")
    .nullable(),
  
  name_prefix: Yup.string().nullable(),
  
  // Contact Information - Required fields
  email: Yup.string()
    .matches(emailRegExp, "Please enter a valid email address")
    .required("Email is required"),
  
  phone: Yup.string()
    .matches(phoneRegExp, "Phone number must be 10-15 digits and may include country code, spaces, dashes, or parentheses")
    .required("Phone number is required"),
  
  // Address fields - Required
  address: Yup.string()
    .min(10, "Address must be at least 10 characters")
    .max(200, "Address must be less than 200 characters")
    .required("Address is required"),
  
  city: Yup.string()
    .min(2, "City must be at least 2 characters")
    .max(50, "City must be less than 50 characters")
    .required("City is required"),
  
  state: Yup.string()
    .matches(nameRegExp, "State name can only contain letters, spaces, apostrophes, periods and hyphens")
    .min(2, "State must be at least 2 characters")
    .max(50, "State must be less than 50 characters")
    .required("State is required"),
  
  // Professional Information - Required fields
  department: Yup.object().shape({
    value: Yup.string().required("Department is required"),
    label: Yup.string().required("Department is required")
  }).nullable().required("Department is required"),
  
  // TODO: Roles validation - commented for future use
  roles: Yup.array().of(
    Yup.object().shape({
      value: Yup.string().required("Role value is required"),
      label: Yup.string().required("Role label is required")
    })
  ).nullable(), // Made optional - commented .min(1, "At least one role must be selected").required("Roles are required"),
  
  designation: Yup.string()
    .min(2, "Designation must be at least 2 characters")
    .max(100, "Designation must be less than 100 characters")
    .required("Designation is required"),
  
  specialty: Yup.string()
    .min(2, "Specialty must be at least 2 characters")
    .max(100, "Specialty must be less than 100 characters")
    .required("Specialty is required"),
  
  // ID Numbers with specific patterns
  employee_id: Yup.string()
    .matches(employeeIdRegExp, "Employee ID must be 3-20 characters, containing only letters, numbers and hyphens")
    .required("Employee ID is required"),
  
  emirates_id_number: Yup.string()
    .matches(emiratesIdRegExp, "Emirates ID must be exactly 15 digits starting with 784")
    .required("Emirates ID is required"),
  
  // TODO: License number validation - commented for future use
  license_number: Yup.string()
    .matches(licenseNumberRegExp, "License number must be 5-30 characters, containing only letters, numbers, hyphens and slashes")
    .nullable(), // Made optional - commented .required("License number is required"),
  
  // Date fields
  date_of_joining: Yup.string()
    .required("Date of joining is required"),
  
  releavingDate: Yup.string().nullable(),
  
  // Gender - required
  gender: Yup.string()
    .oneOf(["1", "2", "3"], "Please select a valid gender")
    .required("Gender is required"),
  
  // Status
  is_active: Yup.boolean().required("Status is required"),
  
  // Optional password fields - only validate if provided
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .nullable(),
  
  confirm_password: Yup.string()
    .nullable()
    .when('password', {
      is: (password) => password && password.length > 0,
      then: () => Yup.string()
        .oneOf([Yup.ref('password'), null], "Password and confirm password must match")
        .required("Confirm password is required when password is provided"),
      otherwise: () => Yup.string().nullable()
    }),
  
  // File uploads are optional in edit mode
  photo: Yup.mixed().nullable(),
  signature: Yup.mixed().nullable(),
  stamp_image: Yup.mixed().nullable()
});

export const profileSchema = Yup.object().shape({
  firstName: Yup.string()
    .min(2, "Too Short Name!")
    .max(50, "Too Long Name!")
    .required("First name is required."),
  lastName: Yup.string()
    .min(2, "Too Short Name!")
    .max(50, "Too Long Name!")
    .required("Last name is required."),
  email: Yup.string().email("Invalid email").required("Email is required."),
  mobileNumber: Yup.string()
    .matches(phoneRegExp, " only Numeric values allowed")
    .required("Phone number is required."),
  address: Yup.string().required("Address is required."),
  gender: Yup.string().required("Gender is required."),
});

export const addDepartmentSchema = Yup.object().shape({
  name: Yup.string().required("Name is required."),
});

export const addDepartmentFormsSchema = Yup.object().shape({
  name: Yup.string().required("Name is required."),
  departmentId: Yup.string().required("Select department."),
});
export const editPasswordSchema = Yup.object().shape({
  oldPassword: Yup.string().required("Current password is required."),
  newPassword: Yup.string()
    .required("New password is required.")
    .min(8, "Password must be at least 8 characters long.")
    .matches(/[0-9]/, "Password requires a number")
    .matches(/[a-z]/, "Password requires a lowercase letter")
    .matches(/[A-Z]/, "Password requires an uppercase letter")
    .matches(/[^\w]/, "Password requires a special character"),
  confirmPassword: Yup.string()
    .oneOf(
      [Yup.ref("newPassword"), null],
      "Passwords do not match"
    )
    .required("Confirm password is required."),
});

// New schema that matches backend field names
export const passwordChangeSchema = Yup.object().shape({
  current_password: Yup.string().required("Current password is required."),
  new_password: Yup.string()
    .required("New password is required.")
    .min(8, "Password must be at least 8 characters long.")
    .matches(/[0-9]/, "Password requires a number")
    .matches(/[a-z]/, "Password requires a lowercase letter")
    .matches(/[A-Z]/, "Password requires an uppercase letter")
    .matches(/[^\w]/, "Password requires a special character"),
  confirm_password: Yup.string()
    .oneOf(
      [Yup.ref("new_password"), null],
      "Passwords do not match"
    )
    .required("Confirm password is required."),
});

// Tab-specific schemas for editStaff component
export const basicInfoSchema = Yup.object().shape({
  name_prefix: Yup.string().nullable(),
  first_name: Yup.string()
    .matches(nameRegExp, "First name can only contain letters, spaces, apostrophes, periods and hyphens")
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must be less than 50 characters")
    .required("First name is required"),
  middle_name: Yup.string()
    .matches(nameRegExp, "Middle name can only contain letters, spaces, apostrophes, periods and hyphens")
    .max(50, "Middle name must be less than 50 characters")
    .nullable(),
  last_name: Yup.string()
    .matches(nameRegExp, "Last name can only contain letters, spaces, apostrophes, periods and hyphens")
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name must be less than 50 characters")
    .required("Last name is required"),
  email: Yup.string()
    .matches(emailRegExp, "Please enter a valid email address")
    .required("Email is required"),
  gender: Yup.string()
    .oneOf(["1", "2", "3"], "Please select a valid gender")
    .required("Gender is required")
});

export const contactSchema = Yup.object().shape({
  phone: Yup.string()
    .matches(phoneRegExp, "Phone number must be 10-15 digits and may include country code, spaces, dashes, or parentheses")
    .required("Phone number is required"),
  address: Yup.string()
    .min(10, "Address must be at least 10 characters")
    .max(200, "Address must be less than 200 characters")
    .required("Address is required"),
  city: Yup.string()
    .min(2, "City must be at least 2 characters")
    .max(50, "City must be less than 50 characters")
    .required("City is required"),
  state: Yup.string()
    .matches(nameRegExp, "State name can only contain letters, spaces, apostrophes, periods and hyphens")
    .min(2, "State must be at least 2 characters")
    .max(50, "State must be less than 50 characters")
    .required("State is required")
});

export const professionalSchema = Yup.object().shape({
  department: Yup.object().shape({
    value: Yup.string().required("Department is required"),
    label: Yup.string().required("Department is required")
  }).nullable().required("Department is required"),
  specialty: Yup.string()
    .min(2, "Specialty must be at least 2 characters")
    .max(100, "Specialty must be less than 100 characters")
    .required("Specialty is required"),
  designation: Yup.string()
    .min(2, "Designation must be at least 2 characters")
    .max(100, "Designation must be less than 100 characters")
    .required("Designation is required"),
  roles: Yup.array().of(
    Yup.object().shape({
      value: Yup.string().required("Role value is required"),
      label: Yup.string().required("Role label is required")
    })
  ).nullable(),
  employee_id: Yup.string()
    .matches(employeeIdRegExp, "Employee ID must be 3-20 characters, containing only letters, numbers and hyphens")
    .required("Employee ID is required"),
  emirates_id_number: Yup.string()
    .matches(emiratesIdRegExp, "Emirates ID must be exactly 15 digits starting with 784")
    .required("Emirates ID is required"),
  license_number: Yup.string()
    .matches(licenseNumberRegExp, "License number must be 5-30 characters, containing only letters, numbers, hyphens and slashes")
    .nullable(),
  date_of_joining: Yup.string()
    .required("Date of joining is required"),
  releavingDate: Yup.string().nullable(),
  is_active: Yup.boolean().required("Status is required")
});

export const securitySchema = Yup.object().shape({
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .nullable(),
  confirm_password: Yup.string()
    .nullable()
    .when('password', {
      is: (password) => password && password.length > 0,
      then: () => Yup.string()
        .oneOf([Yup.ref('password'), null], "Password and confirm password must match")
        .required("Confirm password is required when password is provided"),
      otherwise: () => Yup.string().nullable()
    })
});

export const filesSchema = Yup.object().shape({
  photo: Yup.mixed().nullable(),
  signature: Yup.mixed().nullable(),
  stamp_image: Yup.mixed().nullable()
});

